import { Injectable } from '@angular/core';

import moment from 'moment-timezone';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';

import { CurationAgentsApiService } from '../../api/agents/curation-agents.api.service';
import {
    AgentAdhocSourceResponse,
    CuratorQuotaResponse,
    HourlyUsage
} from '../../api/agents/models/curation-agent.api';
import { DateQueryModel } from '../../Common/Forms/models/date-query.model';

/** Absolute bounds of the window being reported on, epoch ms */
export interface UsageBounds {
    start: number;
    end: number;
}

/** One bucket of a usage series */
export interface UsagePoint {
    /** Epoch ms of the bucket's start */
    timestamp: number;
    count: number;
}

export interface AgentUsageModel {
    total: number;
    /** One entry per bucket over the window, zero filled */
    points: UsagePoint[];
}

export const emptyUsage: AgentUsageModel = { total: 0, points: [] };

/** What an agent did over a window: what it looked at, and what it kept */
export interface AgentActivity {
    analyzed: AgentUsageModel;
    curated: AgentUsageModel;
}

export const emptyActivity: AgentActivity = { analyzed: emptyUsage, curated: emptyUsage };

/** One source's totals over the window */
export interface SourceTotals {
    analyzed: number;
    curated: number;
}

const hourMs: number = 60 * 60 * 1000;
const dayMs: number = 24 * hourMs;

/**
 * Buckets a window is charted in, the way the coverage charts pick their gap: hourly for a window
 * of a day, daily beyond it, and weekly once a window is long enough for daily points to run
 * together. A day's window is a day's point, so three days plot three or four points rather than
 * repeating the same date across every six hours of it.
 *
 * The hourly threshold has a day's headroom because Last 24 Hours measures more than 24 hours - it
 * runs from the top of the hour a day ago to this moment - and the next option up is three days.
 */
const bucketThresholds: { maxSpan: number; bucket: number }[] = [
    { maxSpan: 2 * dayMs, bucket: hourMs },
    { maxSpan: 120 * dayMs, bucket: dayMs }
];
const widestBucket: number = 7 * dayMs;

/** How far back `All` is narrowed to, since the usage endpoint only reports over a bounded range */
export const maxReportingDays: number = 366;

/**
 * What an agent looked at and what it kept, over a window.
 *
 * Both halves come from agents-service's own usage records, which count them in the same hourly
 * buckets - so the two series share a clock. Reading curated from a mention search instead would
 * put them on different ones, and would miss anything the agent curated and then removed.
 */
@Injectable()
export class AgentUsageService {

    constructor(private curationAgentsApi: CurationAgentsApiService) {}

    /** Absolute bounds of a range, or null for `All`, which the usage endpoints cannot express */
    static bounds(dateRange: DateQueryModel): UsageBounds {
        if (!dateRange) {
            return null;
        }

        try {
            const absolute = DateQueryModel.makeAbsoluteRange(dateRange);

            return { start: +absolute.absoluteRange.start, end: +absolute.absoluteRange.end };
        } catch {
            return null;
        }
    }

    /**
     * The window to report over. `All` has no bounds, and the usage endpoint only reports over a
     * range, so it is narrowed to the last year - which reaches back well past the point agents
     * started recording anything.
     */
    static reportingRange(dateRange: DateQueryModel): DateQueryModel {
        if (AgentUsageService.bounds(dateRange)) {
            return dateRange;
        }

        const range = DateQueryModel.getAbsoluteQuery();
        range.absoluteRange = {
            start: new Date(Date.now() - (maxReportingDays * dayMs)),
            end: new Date()
        };

        return range;
    }

    /** The window immediately before this one, for the change badges. Null when there is no such thing */
    static previousPeriod(dateRange: DateQueryModel): DateQueryModel {
        try {
            return DateQueryModel.getPreviousPeriod(dateRange);
        } catch {
            return null;
        }
    }

    static bucketMs(bounds: UsageBounds): number {
        const span = Math.max(bounds.end - bounds.start, hourMs);

        return bucketThresholds.find(threshold => span <= threshold.maxSpan)?.bucket ?? widestBucket;
    }

    /**
     * What the agent did over the window, hour by hour: both series from one read of the usage
     * endpoint, which records the analyzed count and the curated subset in the same buckets.
     *
     * Ad hoc work counts the same way a source does: each run is its own bucket in the response, so
     * it is charted in the hours it was actually processed rather than at the moment it was
     * submitted. A run that has not been processed yet has no usage, and so adds nothing until it is.
     */
    activityForScope(
        dateRange: DateQueryModel,
        agentId: number,
        scope: { sourceIds: number[]; adHocRuns: AgentAdhocSourceResponse[] }
    ): Observable<AgentActivity> {
        const bounds = AgentUsageService.bounds(dateRange);
        const runIds = (scope.adHocRuns ?? []).map(run => run.id);

        if (!bounds || !agentId || (!scope.sourceIds.length && !runIds.length)) {
            return of(emptyActivity);
        }

        // One read for the whole agent: the breakdown carries each source's own hours, and an ad hoc
        // run's too, so everything in scope is summed from the same response
        return this.curationAgentsApi.getQuota({
            period: 'range',
            start: bounds.start,
            end: bounds.end,
            agentId: String(agentId),
            breakdown: 'source'
        }).pipe(
            map((quota: CuratorQuotaResponse) => {
                const inScope = (quota?.usage?.sources ?? []).filter(usage => usage.sourceId !== null
                    ? scope.sourceIds.includes(usage.sourceId)
                    : runIds.includes(usage.adhocId));

                return {
                    analyzed: this.mergeUsage(inScope.map(usage => this.hoursToUsage(usage.hours, bounds, false)), bounds),
                    curated: this.mergeUsage(inScope.map(usage => this.hoursToUsage(usage.hours, bounds, true)), bounds)
                };
            }),
            catchError(() => of(emptyActivity))
        );
    }

    /**
     * The same two series for the agent as a whole, or for one of its sources. `sourceId` takes a
     * source's id, or the literal 'adhoc' for everything submitted by hand.
     */
    activity(dateRange: DateQueryModel, agentId: number, sourceId?: number | 'adhoc'): Observable<AgentActivity> {
        const bounds = AgentUsageService.bounds(dateRange);

        if (!bounds || !agentId) {
            return of(emptyActivity);
        }

        return this.curationAgentsApi.getQuota({
            period: 'range',
            start: bounds.start,
            end: bounds.end,
            agentId: String(agentId),
            ...(sourceId ? { sourceId: String(sourceId) } : {})
        }).pipe(
            map((quota: CuratorQuotaResponse) => ({
                analyzed: this.hoursToUsage(quota?.usage?.hours, bounds, false),
                curated: this.hoursToUsage(quota?.usage?.hours, bounds, true)
            })),
            catchError(() => of(emptyActivity))
        );
    }

    /**
     * Analyzed and curated totals for the window, keyed by source id. Ad hoc buckets have no source
     * of their own, so they are left out.
     */
    totalsBySource(dateRange: DateQueryModel, agentId?: number): Observable<Map<number, SourceTotals>> {
        const bounds = AgentUsageService.bounds(dateRange);

        if (!bounds) {
            return of(new Map<number, SourceTotals>());
        }

        return this.curationAgentsApi.getQuota({
            period: 'range',
            start: bounds.start,
            end: bounds.end,
            ...(agentId ? { agentId: String(agentId) } : {})
        }).pipe(
            map((quota: CuratorQuotaResponse) => {
                const totals = new Map<number, SourceTotals>();

                (quota?.usage?.sources ?? [])
                    .filter(usage => usage.sourceId !== null && (!agentId || usage.agentId === agentId))
                    .forEach(usage => {
                        const entry = totals.get(usage.sourceId) ?? { analyzed: 0, curated: 0 };

                        entry.analyzed += usage.count ?? 0;
                        entry.curated += usage.curatedMentions ?? 0;
                        totals.set(usage.sourceId, entry);
                    });

                return totals;
            }),
            catchError(() => of(new Map<number, SourceTotals>()))
        );
    }

    /** One hourly series read as either half of what the hour recorded */
    private hoursToUsage(hours: HourlyUsage[], bounds: UsageBounds, curated: boolean): AgentUsageModel {
        return this.toUsage(
            (hours ?? []).map(hour => ({
                timestamp: hour.hour,
                count: (curated ? hour.curatedMentions : hour.count) ?? 0
            })),
            bounds
        );
    }


    /** Adds several narrowed reads back together, bucket by bucket */
    private mergeUsage(usages: AgentUsageModel[], bounds: UsageBounds): AgentUsageModel {
        const readings = usages.flatMap(usage => usage.points);
        const merged = this.toUsage(readings, bounds);

        return { total: usages.reduce((sum, usage) => sum + usage.total, 0), points: merged.points };
    }

    /**
     * Lays readings out on one grid of buckets, so series from different endpoints line up on the
     * same chart. Anything outside the window is dropped rather than folded into the end buckets.
     */
    private toUsage(readings: UsagePoint[], bounds: UsageBounds, total?: number): AgentUsageModel {
        const bucket = AgentUsageService.bucketMs(bounds);

        // Buckets sit on the viewer's own calendar, not on epoch arithmetic: flooring milliseconds
        // to a multiple of a day lands on UTC midnight, which is the evening of the day before
        // anywhere west of Greenwich - so a window opening on the 26th would be charted from the
        // 25th. Stepping in days rather than in a fixed width also keeps the grid honest across a
        // daylight saving change, where a day is 23 or 25 hours long.
        const unit: 'hour' | 'day' = bucket <= hourMs ? 'hour' : 'day';
        const step: number = Math.max(1, Math.round(bucket / (unit === 'hour' ? hourMs : dayMs)));
        const start = moment(bounds.start).startOf(unit);
        const bucketIndex = (timestamp: number): number =>
            Math.floor(moment(timestamp).diff(start, unit, true) / step);

        const count = Math.max(1, Math.ceil(moment(bounds.end).diff(start, unit, true) / step));

        const points: UsagePoint[] = Array.from({ length: count }, (unused, index) => ({
            timestamp: +start.clone().add(index * step, unit),
            count: 0
        }));

        let summed = 0;

        readings.forEach(reading => {
            const index = bucketIndex(reading.timestamp);

            summed += reading.count;

            if (index >= 0 && index < points.length) {
                points[index].count += reading.count;
            }
        });

        return { total: total ?? summed, points: points };
    }
}
